/**
 * The line under "Ilość" in the "Magazyn" step (#692 rev B): how the dosing
 * gave the quantity on screen, e.g. "Kuracja 1 tabletka × 2 dziennie przez
 * 7 dni = 14 tabletek → 2 op. (20 tabletek)" or "Jedno podanie: 1 pipeta →
 * 1 op. (3 pipety)", so the vet can trust it at a glance — packages with
 * what they hold (P7B-R12).
 */
import { translateKey } from 'src/locales/translate';
import {
  formatMilli,
  formatUnits,
  unitLabelsFor,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import {
  UnitLabels,
  toMilli,
} from 'src/sections/@dashboard/warehouse/utils/quantity';
import { StockDisplayProduct } from 'src/sections/@dashboard/warehouse/utils/stockDisplay';
import { StockIssueBasis } from './stockIssueComputation';

const labelsOf = (key: string): UnitLabels => ({
  one: translateKey(`${key}.one`),
  few: translateKey(`${key}.few`),
  many: translateKey(`${key}.many`),
  fraction: translateKey(`${key}.fraction`),
});

const counted = (value: number, labels: UnitLabels): string =>
  formatUnits(toMilli(value), labels);

export function stockIssueExplanation(
  basis: StockIssueBasis,
  product: Pick<StockDisplayProduct, 'unit' | 'unitCustom' | 'unitsPerPackage'>,
): string {
  const units = unitLabelsFor(product);
  const dose = counted(basis.dose, units);
  const packages = (
    count: number,
  ): { packages: string; packageContent: string } => ({
    packages: `${count} ${translateKey('warehouse.common.packageShort')}`,
    packageContent: counted(count * product.unitsPerPackage, units),
  });
  if (basis.kind === 'DOSE') {
    return translateKey('warehouse.clinical.issueDose', { dose });
  }
  if (basis.kind === 'DOSE_HANDED_OVER') {
    // "Jedno podanie: 1 pipeta → 1 op. (3 pipety)"; a fraction by the unit
    // says the whole Unit it became.
    if (basis.packages !== null) {
      return translateKey('warehouse.clinical.issueDosePackages', {
        dose,
        ...packages(basis.packages),
      });
    }
    return basis.units === basis.dose
      ? translateKey('warehouse.clinical.issueDose', { dose })
      : translateKey('warehouse.clinical.issueDoseRounded', {
          dose,
          total: counted(basis.units, units),
        });
  }
  const days = labelsOf('warehouse.clinical.issueDays');
  const parts = {
    dose,
    frequency: translateKey(
      `warehouse.clinical.issueFrequency.${basis.frequencyType}`,
      {
        n: formatMilli(toMilli(basis.frequencyValue)),
        days: counted(basis.frequencyValue, days),
      },
    ),
    duration: counted(
      basis.durationValue,
      basis.durationType === 'WEEKS'
        ? labelsOf('warehouse.clinical.issueWeeks')
        : days,
    ),
    total: counted(basis.units, units),
  };
  if (basis.packages === null) {
    return translateKey('warehouse.clinical.issueCourse', parts);
  }
  return translateKey('warehouse.clinical.issueCoursePackages', {
    ...parts,
    ...packages(basis.packages),
  });
}
